import XLSX from 'xlsx-js-style';
import { MenuData } from '../types';
import type { MenuProfile } from '../types';
import { orchestrateExport, type OrchestratedExport, type SharedGroup } from './ExportOrchestrator';

const YELLOW_HEX = "FFF200";
const BLACK_HEX = "000000";
const WHITE_HEX = "FFFFFF";
const PURPLE_DARK_HEX = "7030A0";
const PURPLE_LIGHT_HEX = "BF7AF2";

const borderThin = { style: "thin", color: { rgb: BLACK_HEX } };
const borderMedium = { style: "medium", color: { rgb: BLACK_HEX } };

const baseStyle = {
  font: { name: "Arial", sz: 10, color: { rgb: BLACK_HEX } },
  alignment: { vertical: "center", wrapText: false }, 
  border: { top: borderThin, bottom: borderThin, left: borderThin, right: borderThin }
};

// --- Template 1 Generator (Standard) ---
export const generateTemplate1Worksheet = (data: MenuData, currency: string): XLSX.WorkSheet => {
  const styles = {
    header: { ...baseStyle, font: { ...baseStyle.font, bold: true, sz: 11, color: { rgb: WHITE_HEX } }, fill: { fgColor: { rgb: BLACK_HEX } }, alignment: { horizontal: "center", vertical: "center", wrapText: false }, border: { top: borderMedium, bottom: borderMedium, left: borderMedium, right: borderMedium } },
    categoryStart: { font: { name: "Arial", sz: 11, color: { rgb: BLACK_HEX }, bold: true }, alignment: { vertical: "center", wrapText: false }, border: { top: borderMedium, bottom: borderMedium, left: borderMedium, right: borderMedium }, fill: { fgColor: { rgb: YELLOW_HEX } } },
    categoryEmpty: { font: { name: "Arial", sz: 11 }, alignment: { vertical: "center", wrapText: false }, border: {}, fill: { fgColor: { rgb: WHITE_HEX } } },
    categoryMiddle: { font: { name: "Arial", sz: 11, color: { rgb: BLACK_HEX }, bold: true }, alignment: { vertical: "center", wrapText: false }, border: { top: borderMedium, bottom: borderMedium }, fill: { fgColor: { rgb: YELLOW_HEX } } },
    categoryEnd: { font: { name: "Arial", sz: 11, color: { rgb: BLACK_HEX }, bold: true }, alignment: { vertical: "center", wrapText: false }, border: { top: borderMedium, bottom: borderMedium, right: borderMedium }, fill: { fgColor: { rgb: YELLOW_HEX } } },
    itemName: { ...baseStyle, fill: { fgColor: { rgb: WHITE_HEX } }, alignment: { horizontal: "center", vertical: "center", wrapText: false } },
    itemDescYellow: { ...baseStyle, fill: { fgColor: { rgb: WHITE_HEX } } },
    groupHeaderLeft: { ...baseStyle, font: { ...baseStyle.font, bold: true, color: { rgb: WHITE_HEX } }, fill: { fgColor: { rgb: BLACK_HEX } }, alignment: { horizontal: "left", vertical: "center" } },
    groupHeaderRight: { ...baseStyle, font: { ...baseStyle.font, bold: true, color: { rgb: WHITE_HEX } }, fill: { fgColor: { rgb: BLACK_HEX } }, alignment: { horizontal: "right", vertical: "center" } },
    groupHeaderCenter: { ...baseStyle, font: { ...baseStyle.font, bold: true, color: { rgb: WHITE_HEX } }, fill: { fgColor: { rgb: BLACK_HEX } }, alignment: { horizontal: "center", vertical: "center" } },
    groupHeaderEmpty: { ...baseStyle, fill: { fgColor: { rgb: BLACK_HEX } } },
    option: { ...baseStyle, fill: { fgColor: { rgb: WHITE_HEX } } }
  };

  const headers = ['Category - EN', 'Category - AR', 'Item Name - EN', 'Item Name - AR', 'Description - EN', 'Description - AR', 'Item Price', 'Max', 'Min'];
  const dataRows: any[][] = [];
  const merges: any[] = [];
  const cellStyles: Record<string, any> = {};
  const setStyle = (r: number, c: number, style: any) => { cellStyles[`${r}_${c}`] = style; };

  dataRows.push(headers);
  headers.forEach((_, c) => setStyle(0, c, styles.header));

  let r = 1;
  data.categories.forEach(cat => {
    const catRow = Array(9).fill('');
    catRow[0] = cat.name.en;
    catRow[1] = cat.name.ar || '';
    dataRows.push(catRow);
    setStyle(r, 0, { ...styles.categoryStart, alignment: { ...styles.categoryStart.alignment, horizontal: 'left' } });
    setStyle(r, 1, { ...styles.categoryStart, alignment: { ...styles.categoryStart.alignment, horizontal: 'right' } });
    for(let c=2; c<=8; c++) {
        if (c <= 7) setStyle(r, c, styles.categoryMiddle);
        else setStyle(r, c, styles.categoryEnd);
    }
    r++;

    cat.items.forEach((item) => {
      let itemRows = 1;
      item.choiceGroups.forEach(cg => { itemRows += 1 + cg.options.length; });
      const itemStartRow = r;
      const itemEndRow = r + itemRows - 1;

      merges.push({ s: { r: itemStartRow, c: 2 }, e: { r: itemEndRow, c: 2 } });
      merges.push({ s: { r: itemStartRow, c: 3 }, e: { r: itemEndRow, c: 3 } });

      const row = Array(9).fill('');
      row[2] = item.name.en; row[3] = item.name.ar || ''; row[4] = item.description.en; row[5] = item.description.ar || ''; row[6] = `${currency} ${item.price.toFixed(2)}`;
      dataRows.push(row);

      setStyle(r, 0, styles.categoryEmpty); setStyle(r, 1, styles.categoryEmpty);
      setStyle(r, 2, styles.itemName); setStyle(r, 3, styles.itemName);
      setStyle(r, 4, { ...styles.itemDescYellow, alignment: { ...styles.itemDescYellow.alignment, horizontal: 'left' } });
      setStyle(r, 5, { ...styles.itemDescYellow, alignment: { ...styles.itemDescYellow.alignment, horizontal: 'right' } });
      setStyle(r, 6, { ...styles.itemDescYellow, alignment: { ...styles.itemDescYellow.alignment, horizontal: 'center' } });
      setStyle(r, 7, styles.itemDescYellow); setStyle(r, 8, styles.itemDescYellow);
      r++;

      item.choiceGroups.forEach(cg => {
         const hRow = Array(9).fill('');
         hRow[4] = cg.name.en; hRow[5] = cg.name.ar || ''; hRow[7] = cg.max; hRow[8] = cg.min;
         dataRows.push(hRow);
         setStyle(r, 0, styles.categoryEmpty); setStyle(r, 1, styles.categoryEmpty); setStyle(r, 2, styles.itemName); setStyle(r, 3, styles.itemName);
         setStyle(r, 4, styles.groupHeaderLeft); setStyle(r, 5, styles.groupHeaderRight); setStyle(r, 6, styles.groupHeaderEmpty); setStyle(r, 7, styles.groupHeaderCenter); setStyle(r, 8, styles.groupHeaderCenter);
         r++;

         cg.options.forEach(opt => {
            const oRow = Array(9).fill('');
            oRow[4] = opt.name.en; oRow[5] = opt.name.ar || ''; oRow[6] = opt.priceModifier ? `${currency} ${opt.priceModifier.toFixed(2)}` : `${currency} -`;
            dataRows.push(oRow);
            setStyle(r, 0, styles.categoryEmpty); setStyle(r, 1, styles.categoryEmpty); setStyle(r, 2, styles.itemName); setStyle(r, 3, styles.itemName);
            setStyle(r, 4, { ...styles.option, alignment: { ...styles.option.alignment, horizontal: 'left' } });
            setStyle(r, 5, { ...styles.option, alignment: { ...styles.option.alignment, horizontal: 'right' } });
            setStyle(r, 6, { ...styles.option, alignment: { ...styles.option.alignment, horizontal: 'center' } });
            setStyle(r, 7, styles.option); setStyle(r, 8, styles.option);
            r++;
         });
      });
    });
  });

  const ws = XLSX.utils.aoa_to_sheet(dataRows);
  ws['!merges'] = merges;
  Object.keys(cellStyles).forEach(key => {
     const [row, col] = key.split('_').map(Number);
     const cellRef = XLSX.utils.encode_cell({ r: row, c: col });
     if(!ws[cellRef]) XLSX.utils.sheet_add_aoa(ws, [['']], { origin: cellRef });
     if(ws[cellRef]) ws[cellRef].s = cellStyles[key];
  });
  ws['!cols'] = [{ wch: 20 }, { wch: 20 }, { wch: 25 }, { wch: 25 }, { wch: 45 }, { wch: 40 }, { wch: 15 }, { wch: 10 }, { wch: 10 }];
  return ws;
};

// --- Template 2 Generator (Purple Headers, Aggregated Addons) ---
export const generateTemplate2Worksheet = (data: MenuData, currency: string): XLSX.WorkSheet => {
  const dataRows: any[][] = [];
  const merges: any[] = [];
  const cellStyles: Record<string, any> = {};
  const setStyle = (r: number, c: number, style: any) => { cellStyles[`${r}_${c}`] = style; };

  const vendorBarStyle = { fill: { fgColor: { rgb: PURPLE_DARK_HEX } }, font: { name: "Calibri", sz: 11, color: { rgb: WHITE_HEX } }, alignment: { horizontal: "left", vertical: "center" }, border: { top: borderMedium, bottom: borderMedium, left: borderMedium, right: borderMedium } };
  const headerStyle = { fill: { fgColor: { rgb: PURPLE_LIGHT_HEX } }, font: { name: "Arial Narrow", sz: 10, bold: true, color: { rgb: WHITE_HEX } }, alignment: { horizontal: "center", vertical: "center", wrapText: true }, border: { bottom: borderThin } };
  const dataStyleBase = { font: { name: "Arial", sz: 10, color: { rgb: BLACK_HEX } }, alignment: { vertical: "center", wrapText: false }, border: { bottom: borderThin, left: borderThin, right: borderThin } };

  const vendorName = `VENDOR NAME: ${data.brandName || data.title?.en || 'Menu'}`;
  const branchName = `BRANCH: All Branches`;
  const row1 = Array(10).fill('');
  row1[0] = vendorName; row1[2] = branchName;
  dataRows.push(row1);
  merges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: 1 } });
  merges.push({ s: { r: 0, c: 2 }, e: { r: 0, c: 3 } });

  for (let c = 0; c < 10; c++) {
    if (c >= 4) setStyle(0, c, { ...vendorBarStyle, fill: { fgColor: { rgb: WHITE_HEX } } });
    else setStyle(0, c, vendorBarStyle);
  }

  const headers = ['CATEGORY - ENGLISH', 'CATEGORY - ARABIC', 'PRODUCT NAME - ENGLISH', 'PRODUCT NAME - ARABIC', 'DESCRIPTION - ENGLISH', 'DESCRIPTION - ARABIC', 'PRICE', 'PREPARATION TIME', 'ADD-ONS ENGLISH', 'ADD-ONS ARABIC'];
  dataRows.push(headers);
  dataRows.push(Array(10).fill(''));
  for (let c = 0; c < 10; c++) {
    merges.push({ s: { r: 1, c: c }, e: { r: 2, c: c } });
    let style = { ...headerStyle, border: { ...headerStyle.border } };
    if (c >= 4) (style.border as any).top = borderMedium; else delete (style.border as any).top;
    if (c === 0) (style.border as any).left = borderMedium; if (c === 9) (style.border as any).right = borderMedium;
    setStyle(1, c, style); setStyle(2, c, style);
  }

  let r = 3;
  data.categories.forEach(cat => {
    cat.items.forEach(item => {
      const row = Array(10).fill('');
      row[0] = cat.name.en; row[1] = cat.name.ar || ''; row[2] = item.name.en; row[3] = item.name.ar || ''; row[4] = item.description.en; row[5] = item.description.ar || ''; row[6] = `${currency} ${item.price.toFixed(2)}`;
      
      const addOnsEnParts: string[] = [];
      const addOnsArParts: string[] = [];
      item.choiceGroups.forEach(cg => {
          const groupEn = cg.name.en;
          const groupAr = cg.name.ar || cg.name.en;
          const optionsEn = cg.options.map(opt => `${opt.name.en}(${opt.priceModifier || 0})`).join(',');
          const optionsAr = cg.options.map(opt => `${opt.name.ar || opt.name.en}(${opt.priceModifier || 0})`).join(',');
          if (optionsEn) addOnsEnParts.push(`${groupEn}-${optionsEn}`);
          if (optionsAr) addOnsArParts.push(`${groupAr}-${optionsAr}`);
      });
      row[8] = addOnsEnParts.join(' / ');
      row[9] = addOnsArParts.join(' / ');
      dataRows.push(row);

      for (let c = 0; c < 10; c++) {
        let align = 'left'; if ([1, 3, 5, 9].includes(c)) align = 'right';
        setStyle(r, c, { ...dataStyleBase, alignment: { ...dataStyleBase.alignment, horizontal: align } });
      }
      r++;
    });
  });

  const ws = XLSX.utils.aoa_to_sheet(dataRows);
  ws['!merges'] = merges;
  Object.keys(cellStyles).forEach(key => {
     const [row, col] = key.split('_').map(Number);
     const cellRef = XLSX.utils.encode_cell({ r: row, c: col });
     if(!ws[cellRef]) XLSX.utils.sheet_add_aoa(ws, [['']], { origin: cellRef });
     if(ws[cellRef]) ws[cellRef].s = cellStyles[key];
  });
  ws['!cols'] = [ { wch: 20 }, { wch: 20 }, { wch: 25 }, { wch: 25 }, { wch: 50 }, { wch: 50 }, { wch: 15 }, { wch: 15 }, { wch: 20 }, { wch: 20 } ];
  ws['!rows'] = []; ws['!rows'][0] = { hpt: 19.5 }; ws['!rows'][2] = { hpt: 15.75 };
  return ws;
};

// --- Export Functions ---
export const exportTemplate1 = (data: MenuData, filename: string, currency: string) => {
    const ws = generateTemplate1Worksheet(data, currency);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Menu Data');
    XLSX.writeFile(wb, `${filename}.xlsx`);
};

export const exportTemplate2 = (data: MenuData, filename: string, currency: string) => {
    const ws = generateTemplate2Worksheet(data, currency);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, `${filename}.xlsx`);
};

export const exportTemplateBoth = (data: MenuData, filename: string, currency: string) => {
    const ws1 = generateTemplate1Worksheet(data, currency);
    const ws2 = generateTemplate2Worksheet(data, currency);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws1, 'Menu Data');
    XLSX.utils.book_append_sheet(wb, ws2, 'Sheet1');
    XLSX.writeFile(wb, `${filename}.xlsx`);
};

// ═══════════════════════════════════════════════════════════════════════════════
// RELATIONAL EXPORTS — Shared Modifier Library (Sheet 2)
// ═══════════════════════════════════════════════════════════════════════════════

const TEAL_HEX = '0D9488';

function generateSharedModifierSheet(sharedGroups: SharedGroup[], currency: string): XLSX.WorkSheet {
  const headerStyle = {
    font: { name: 'Arial', sz: 10, bold: true, color: { rgb: WHITE_HEX } },
    fill: { fgColor: { rgb: BLACK_HEX } },
    alignment: { horizontal: 'center' as const, vertical: 'center' as const, wrapText: true },
    border: { top: borderMedium, bottom: borderMedium, left: borderMedium, right: borderMedium },
  };
  const groupNameStyle = {
    font: { name: 'Arial', sz: 10, bold: true, color: { rgb: BLACK_HEX } },
    alignment: { horizontal: 'center' as const, vertical: 'center' as const, wrapText: false },
    border: { top: borderThin, bottom: borderThin, left: borderThin, right: borderThin },
    fill: { fgColor: { rgb: WHITE_HEX } },
  };
  const optionStyle = {
    font: { name: 'Arial', sz: 10, color: { rgb: BLACK_HEX } },
    alignment: { vertical: 'center' as const, wrapText: false },
    border: { top: borderThin, bottom: borderThin, left: borderThin, right: borderThin },
    fill: { fgColor: { rgb: WHITE_HEX } },
  };
  const optionPriceStyle = {
    ...optionStyle,
    alignment: { horizontal: 'center' as const, vertical: 'center' as const, wrapText: false },
  };
  const separatorStyle = {
    font: { name: 'Arial', sz: 4, color: { rgb: BLACK_HEX } },
    fill: { fgColor: { rgb: BLACK_HEX } },
    border: { top: borderThin, bottom: borderThin, left: borderThin, right: borderThin },
  };
  const emptyGroupCell = {
    font: { name: 'Arial', sz: 10 },
    alignment: { vertical: 'center' as const },
    border: { top: borderThin, bottom: borderThin, left: borderThin, right: borderThin },
    fill: { fgColor: { rgb: WHITE_HEX } },
  };

  const headers = ['Group Name (EN)', 'Group Name (AR)', 'Min', 'Max', 'Option Name (EN)', 'Option Name (AR)', 'Option Price'];
  const rows: any[][] = [headers];
  const merges: any[] = [];
  const cellStyles: Record<string, any> = {};
  const setStyle = (r: number, c: number, s: any) => { cellStyles[`${r}_${c}`] = s; };

  headers.forEach((_, c) => setStyle(0, c, headerStyle));

  let r = 1;
  for (let gi = 0; gi < sharedGroups.length; gi++) {
    const grp = sharedGroups[gi];
    const visibleOpts = grp.options;
    const optCount = Math.max(visibleOpts.length, 1);
    const groupStartRow = r;

    for (let i = 0; i < optCount; i++) {
      const row = Array(7).fill('');
      if (i === 0) {
        row[0] = grp.nameEN; row[1] = grp.nameAR;
        row[2] = grp.min; row[3] = grp.max;
      }
      if (visibleOpts[i]) {
        row[4] = visibleOpts[i].nameEN; row[5] = visibleOpts[i].nameAR;
        row[6] = visibleOpts[i].extraPrice ? `${currency} ${visibleOpts[i].extraPrice.toFixed(2)}` : `${currency} 0.00`;
      }
      rows.push(row);
      // Style: group name cols get groupNameStyle on first row, empty on rest
      setStyle(r, 0, i === 0 ? groupNameStyle : emptyGroupCell);
      setStyle(r, 1, i === 0 ? groupNameStyle : emptyGroupCell);
      setStyle(r, 2, i === 0 ? groupNameStyle : emptyGroupCell);
      setStyle(r, 3, i === 0 ? groupNameStyle : emptyGroupCell);
      setStyle(r, 4, optionStyle);
      setStyle(r, 5, optionStyle);
      setStyle(r, 6, optionPriceStyle);
      r++;
    }

    // Merge group name, AR, min, max vertically if >1 option
    if (optCount > 1) {
      merges.push({ s: { r: groupStartRow, c: 0 }, e: { r: groupStartRow + optCount - 1, c: 0 } });
      merges.push({ s: { r: groupStartRow, c: 1 }, e: { r: groupStartRow + optCount - 1, c: 1 } });
      merges.push({ s: { r: groupStartRow, c: 2 }, e: { r: groupStartRow + optCount - 1, c: 2 } });
      merges.push({ s: { r: groupStartRow, c: 3 }, e: { r: groupStartRow + optCount - 1, c: 3 } });
    }

    // Black separator row between groups (not after last)
    if (gi < sharedGroups.length - 1) {
      const sepRow = Array(7).fill('');
      rows.push(sepRow);
      for (let c = 0; c < 7; c++) setStyle(r, c, separatorStyle);
      r++;
    }
  }

  if (sharedGroups.length === 0) {
    rows.push(['No shared modifier groups found.', '', '', '', '', '', '']);
    for (let c = 0; c < 7; c++) setStyle(r, c, optionStyle);
  }

  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws['!merges'] = merges;
  Object.keys(cellStyles).forEach(key => {
    const [row, col] = key.split('_').map(Number);
    const ref = XLSX.utils.encode_cell({ r: row, c: col });
    if (!ws[ref]) XLSX.utils.sheet_add_aoa(ws, [['']], { origin: ref });
    if (ws[ref]) ws[ref].s = cellStyles[key];
  });
  ws['!cols'] = [{ wch: 28 }, { wch: 28 }, { wch: 8 }, { wch: 8 }, { wch: 28 }, { wch: 28 }, { wch: 14 }];
  return ws;
}

// ── Template 1 Relational (Detailed + Shared Modifier Library) ──

function generateRelationalTemplate1Worksheet(orch: OrchestratedExport, currency: string): XLSX.WorkSheet {
  const styles = {
    header: { ...baseStyle, font: { ...baseStyle.font, bold: true, sz: 11, color: { rgb: WHITE_HEX } }, fill: { fgColor: { rgb: BLACK_HEX } }, alignment: { horizontal: "center", vertical: "center", wrapText: false }, border: { top: borderMedium, bottom: borderMedium, left: borderMedium, right: borderMedium } },
    categoryStart: { font: { name: "Arial", sz: 11, color: { rgb: BLACK_HEX }, bold: true }, alignment: { vertical: "center", wrapText: false }, border: { top: borderMedium, bottom: borderMedium, left: borderMedium, right: borderMedium }, fill: { fgColor: { rgb: YELLOW_HEX } } },
    categoryMiddle: { font: { name: "Arial", sz: 11, color: { rgb: BLACK_HEX }, bold: true }, alignment: { vertical: "center", wrapText: false }, border: { top: borderMedium, bottom: borderMedium }, fill: { fgColor: { rgb: YELLOW_HEX } } },
    categoryEnd: { font: { name: "Arial", sz: 11, color: { rgb: BLACK_HEX }, bold: true }, alignment: { vertical: "center", wrapText: false }, border: { top: borderMedium, bottom: borderMedium, right: borderMedium }, fill: { fgColor: { rgb: YELLOW_HEX } } },
    categoryEmpty: { font: { name: "Arial", sz: 11 }, alignment: { vertical: "center", wrapText: false }, border: {}, fill: { fgColor: { rgb: WHITE_HEX } } },
    itemName: { ...baseStyle, fill: { fgColor: { rgb: WHITE_HEX } }, alignment: { horizontal: "center", vertical: "center", wrapText: false } },
    itemDescYellow: { ...baseStyle, fill: { fgColor: { rgb: WHITE_HEX } } },
    groupHeaderLeft: { ...baseStyle, font: { ...baseStyle.font, bold: true, color: { rgb: WHITE_HEX } }, fill: { fgColor: { rgb: BLACK_HEX } }, alignment: { horizontal: "left", vertical: "center" } },
    groupHeaderRight: { ...baseStyle, font: { ...baseStyle.font, bold: true, color: { rgb: WHITE_HEX } }, fill: { fgColor: { rgb: BLACK_HEX } }, alignment: { horizontal: "right", vertical: "center" } },
    groupHeaderCenter: { ...baseStyle, font: { ...baseStyle.font, bold: true, color: { rgb: WHITE_HEX } }, fill: { fgColor: { rgb: BLACK_HEX } }, alignment: { horizontal: "center", vertical: "center" } },
    groupHeaderEmpty: { ...baseStyle, fill: { fgColor: { rgb: BLACK_HEX } } },
    option: { ...baseStyle, fill: { fgColor: { rgb: WHITE_HEX } } },
    sharedRef: { ...baseStyle, font: { ...baseStyle.font, bold: true, color: { rgb: TEAL_HEX } }, fill: { fgColor: { rgb: 'F0FDFA' } }, alignment: { horizontal: "left", vertical: "center" } },
    sharedRefRight: { ...baseStyle, font: { ...baseStyle.font, bold: true, color: { rgb: TEAL_HEX } }, fill: { fgColor: { rgb: 'F0FDFA' } }, alignment: { horizontal: "right", vertical: "center" } },
    sharedRefCenter: { ...baseStyle, font: { ...baseStyle.font, bold: true, color: { rgb: TEAL_HEX } }, fill: { fgColor: { rgb: 'F0FDFA' } }, alignment: { horizontal: "center", vertical: "center" } },
    sharedRefEmpty: { ...baseStyle, fill: { fgColor: { rgb: 'F0FDFA' } } },
  };

  // Build lookup for shared group min/max by key
  const sharedMap = new Map(orch.sharedGroups.map(sg => [sg.key, sg]));

  const headers = ['Category - EN', 'Category - AR', 'Item Name - EN', 'Item Name - AR', 'Description - EN', 'Description - AR', 'Item Price', 'Max', 'Min'];
  const COLS = 9;
  const dataRows: any[][] = [];
  const merges: any[] = [];
  const cellStyles: Record<string, any> = {};
  const setStyle = (r: number, c: number, style: any) => { cellStyles[`${r}_${c}`] = style; };

  dataRows.push(headers);
  headers.forEach((_, c) => setStyle(0, c, styles.header));

  let r = 1;
  for (const cat of orch.categories) {
    const catRow = Array(COLS).fill('');
    catRow[0] = cat.nameEN; catRow[1] = cat.nameAR;
    dataRows.push(catRow);
    setStyle(r, 0, { ...styles.categoryStart, alignment: { ...styles.categoryStart.alignment, horizontal: 'left' } });
    setStyle(r, 1, { ...styles.categoryStart, alignment: { ...styles.categoryStart.alignment, horizontal: 'right' } });
    for (let c = 2; c < COLS; c++) {
      if (c < COLS - 1) setStyle(r, c, styles.categoryMiddle);
      else setStyle(r, c, styles.categoryEnd);
    }
    r++;

    for (const item of cat.items) {
      // Calculate total rows: base + unique groups + shared section
      let itemRows = 1;
      item.uniqueGroups.forEach(ug => { itemRows += 1 + ug.options.length; });
      if (item.sharedGroupCodes.length > 0) {
        itemRows += 1 + item.sharedGroupCodes.length; // banner row + each shared ref
      }
      const itemStartRow = r;
      const itemEndRow = r + itemRows - 1;

      merges.push({ s: { r: itemStartRow, c: 2 }, e: { r: itemEndRow, c: 2 } });
      merges.push({ s: { r: itemStartRow, c: 3 }, e: { r: itemEndRow, c: 3 } });

      // Item base row
      const row = Array(COLS).fill('');
      row[2] = item.nameEN; row[3] = item.nameAR;
      row[4] = item.descEN; row[5] = item.descAR;
      row[6] = `${currency} ${item.price.toFixed(2)}`;
      dataRows.push(row);

      setStyle(r, 0, styles.categoryEmpty); setStyle(r, 1, styles.categoryEmpty);
      setStyle(r, 2, styles.itemName); setStyle(r, 3, styles.itemName);
      setStyle(r, 4, { ...styles.itemDescYellow, alignment: { ...styles.itemDescYellow.alignment, horizontal: 'left' } });
      setStyle(r, 5, { ...styles.itemDescYellow, alignment: { ...styles.itemDescYellow.alignment, horizontal: 'right' } });
      setStyle(r, 6, { ...styles.itemDescYellow, alignment: { ...styles.itemDescYellow.alignment, horizontal: 'center' } });
      setStyle(r, 7, styles.itemDescYellow); setStyle(r, 8, styles.itemDescYellow);
      r++;

      // Unique groups inline
      for (const ug of item.uniqueGroups) {
        const hRow = Array(COLS).fill('');
        hRow[4] = ug.nameEN; hRow[5] = ug.nameAR; hRow[7] = ug.max; hRow[8] = ug.min;
        dataRows.push(hRow);
        setStyle(r, 0, styles.categoryEmpty); setStyle(r, 1, styles.categoryEmpty);
        setStyle(r, 2, styles.itemName); setStyle(r, 3, styles.itemName);
        setStyle(r, 4, styles.groupHeaderLeft); setStyle(r, 5, styles.groupHeaderRight);
        setStyle(r, 6, styles.groupHeaderEmpty); setStyle(r, 7, styles.groupHeaderCenter);
        setStyle(r, 8, styles.groupHeaderCenter);
        r++;

        for (const opt of ug.options) {
          const oRow = Array(COLS).fill('');
          oRow[4] = opt.nameEN; oRow[5] = opt.nameAR;
          oRow[6] = opt.extraPrice ? `${currency} ${opt.extraPrice.toFixed(2)}` : `${currency} -`;
          dataRows.push(oRow);
          setStyle(r, 0, styles.categoryEmpty); setStyle(r, 1, styles.categoryEmpty);
          setStyle(r, 2, styles.itemName); setStyle(r, 3, styles.itemName);
          setStyle(r, 4, { ...styles.option, alignment: { ...styles.option.alignment, horizontal: 'left' } });
          setStyle(r, 5, { ...styles.option, alignment: { ...styles.option.alignment, horizontal: 'right' } });
          setStyle(r, 6, { ...styles.option, alignment: { ...styles.option.alignment, horizontal: 'center' } });
          setStyle(r, 7, styles.option); setStyle(r, 8, styles.option);
          r++;
        }
      }

      // Shared modifier groups — inline below unique groups
      if (item.sharedGroupCodes.length > 0) {
        // Black banner row: "LINKED MODIFIER GROUPS" merged across cols 4-8
        const bannerRow = Array(COLS).fill('');
        bannerRow[4] = 'LINKED MODIFIER GROUPS';
        dataRows.push(bannerRow);
        setStyle(r, 0, styles.categoryEmpty); setStyle(r, 1, styles.categoryEmpty);
        setStyle(r, 2, styles.itemName); setStyle(r, 3, styles.itemName);
        for (let c = 4; c < COLS; c++) setStyle(r, c, styles.groupHeaderCenter);
        merges.push({ s: { r, c: 4 }, e: { r, c: 8 } });
        r++;

        // Each shared group reference row
        for (let si = 0; si < item.sharedGroupCodes.length; si++) {
          const code = item.sharedGroupCodes[si];
          const sg = sharedMap.get(code);
          const nameEN = item.sharedGroupNames[si]?.en || code;
          const nameAR = item.sharedGroupNames[si]?.ar || '';

          const sRow = Array(COLS).fill('');
          sRow[4] = nameEN; sRow[5] = nameAR;
          sRow[7] = sg?.max ?? 0; sRow[8] = sg?.min ?? 0;
          dataRows.push(sRow);
          setStyle(r, 0, styles.categoryEmpty); setStyle(r, 1, styles.categoryEmpty);
          setStyle(r, 2, styles.itemName); setStyle(r, 3, styles.itemName);
          setStyle(r, 4, styles.sharedRef); setStyle(r, 5, styles.sharedRefRight);
          setStyle(r, 6, styles.sharedRefEmpty);
          setStyle(r, 7, styles.sharedRefCenter); setStyle(r, 8, styles.sharedRefCenter);
          r++;
        }
      }
    }
  }

  const ws = XLSX.utils.aoa_to_sheet(dataRows);
  ws['!merges'] = merges;
  Object.keys(cellStyles).forEach(key => {
    const [row, col] = key.split('_').map(Number);
    const cellRef = XLSX.utils.encode_cell({ r: row, c: col });
    if (!ws[cellRef]) XLSX.utils.sheet_add_aoa(ws, [['']], { origin: cellRef });
    if (ws[cellRef]) ws[cellRef].s = cellStyles[key];
  });
  ws['!cols'] = [{ wch: 20 }, { wch: 20 }, { wch: 25 }, { wch: 25 }, { wch: 45 }, { wch: 40 }, { wch: 15 }, { wch: 10 }, { wch: 10 }];
  return ws;
}

// ── Template 2 Relational (Aggregator + Shared Modifier Library) ──

function generateRelationalTemplate2Worksheet(orch: OrchestratedExport, currency: string): XLSX.WorkSheet {
  const dataRows: any[][] = [];
  const merges: any[] = [];
  const cellStyles: Record<string, any> = {};
  const setStyle = (r: number, c: number, style: any) => { cellStyles[`${r}_${c}`] = style; };

  const vendorBarStyle = { fill: { fgColor: { rgb: PURPLE_DARK_HEX } }, font: { name: "Calibri", sz: 11, color: { rgb: WHITE_HEX } }, alignment: { horizontal: "left", vertical: "center" }, border: { top: borderMedium, bottom: borderMedium, left: borderMedium, right: borderMedium } };
  const headerStyle = { fill: { fgColor: { rgb: PURPLE_LIGHT_HEX } }, font: { name: "Arial Narrow", sz: 10, bold: true, color: { rgb: WHITE_HEX } }, alignment: { horizontal: "center", vertical: "center", wrapText: true }, border: { bottom: borderThin } };
  const dataStyleBase = { font: { name: "Arial", sz: 10, color: { rgb: BLACK_HEX } }, alignment: { vertical: "center", wrapText: false }, border: { bottom: borderThin, left: borderThin, right: borderThin } };

  const vendorName = `VENDOR NAME: ${orch.brandName || orch.profileName || 'Menu'}`;
  const branchName = `BRANCH: All Branches`;
  const row1 = Array(10).fill('');
  row1[0] = vendorName; row1[2] = branchName;
  dataRows.push(row1);
  merges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: 1 } });
  merges.push({ s: { r: 0, c: 2 }, e: { r: 0, c: 3 } });

  for (let c = 0; c < 10; c++) {
    if (c >= 4) setStyle(0, c, { ...vendorBarStyle, fill: { fgColor: { rgb: WHITE_HEX } } });
    else setStyle(0, c, vendorBarStyle);
  }

  // Use LINKED MODIFIER GROUPS column name when shared groups exist
  const addonsColEN = orch.hasSharedGroups ? 'LINKED MODIFIER GROUPS (EN)' : 'ADD-ONS ENGLISH';
  const addonsColAR = orch.hasSharedGroups ? 'LINKED MODIFIER GROUPS (AR)' : 'ADD-ONS ARABIC';
  const headers = ['CATEGORY - ENGLISH', 'CATEGORY - ARABIC', 'PRODUCT NAME - ENGLISH', 'PRODUCT NAME - ARABIC', 'DESCRIPTION - ENGLISH', 'DESCRIPTION - ARABIC', 'PRICE', 'PREPARATION TIME', addonsColEN, addonsColAR];
  dataRows.push(headers);
  dataRows.push(Array(10).fill(''));
  for (let c = 0; c < 10; c++) {
    merges.push({ s: { r: 1, c: c }, e: { r: 2, c: c } });
    let style = { ...headerStyle, border: { ...headerStyle.border } };
    if (c >= 4) (style.border as any).top = borderMedium; else delete (style.border as any).top;
    if (c === 0) (style.border as any).left = borderMedium; if (c === 9) (style.border as any).right = borderMedium;
    setStyle(1, c, style); setStyle(2, c, style);
  }

  let r = 3;
  for (const cat of orch.categories) {
    for (const item of cat.items) {
      const row = Array(10).fill('');
      row[0] = cat.nameEN; row[1] = cat.nameAR;
      row[2] = item.nameEN; row[3] = item.nameAR;
      row[4] = item.descEN; row[5] = item.descAR;
      row[6] = `${currency} ${item.price.toFixed(2)}`;

      // Build inline add-ons: unique groups inline, shared groups as name-only
      const addOnsEnParts: string[] = [];
      const addOnsArParts: string[] = [];

      // Unique groups: full inline with options
      for (const ug of item.uniqueGroups) {
        const optEn = ug.options.map(o => `${o.nameEN}(${o.extraPrice || 0})`).join(',');
        const optAr = ug.options.map(o => `${o.nameAR || o.nameEN}(${o.extraPrice || 0})`).join(',');
        if (optEn) addOnsEnParts.push(`${ug.nameEN}-${optEn}`);
        if (optAr) addOnsArParts.push(`${ug.nameAR || ug.nameEN}-${optAr}`);
      }

      // Shared groups: name only (no options — they're on Sheet 2)
      for (let i = 0; i < item.sharedGroupCodes.length; i++) {
        const name = item.sharedGroupNames[i];
        addOnsEnParts.push(`[SHARED] ${name.en}`);
        addOnsArParts.push(`[SHARED] ${name.ar || name.en}`);
      }

      row[8] = addOnsEnParts.join(' / ');
      row[9] = addOnsArParts.join(' / ');
      dataRows.push(row);

      for (let c = 0; c < 10; c++) {
        let align = 'left'; if ([1, 3, 5, 9].includes(c)) align = 'right';
        setStyle(r, c, { ...dataStyleBase, alignment: { ...dataStyleBase.alignment, horizontal: align } });
      }
      r++;
    }
  }

  const ws = XLSX.utils.aoa_to_sheet(dataRows);
  ws['!merges'] = merges;
  Object.keys(cellStyles).forEach(key => {
    const [row, col] = key.split('_').map(Number);
    const cellRef = XLSX.utils.encode_cell({ r: row, c: col });
    if (!ws[cellRef]) XLSX.utils.sheet_add_aoa(ws, [['']], { origin: cellRef });
    if (ws[cellRef]) ws[cellRef].s = cellStyles[key];
  });
  ws['!cols'] = [{ wch: 20 }, { wch: 20 }, { wch: 25 }, { wch: 25 }, { wch: 50 }, { wch: 50 }, { wch: 15 }, { wch: 15 }, { wch: 35 }, { wch: 35 }];
  ws['!rows'] = []; ws['!rows'][0] = { hpt: 19.5 }; ws['!rows'][2] = { hpt: 15.75 };
  return ws;
}

// ─── Relational Export Public API ───────────────────────────────────────────

export const exportRelationalTemplate1 = (profile: MenuProfile, filename: string, currency: string) => {
  const orch = orchestrateExport(profile);
  const wb = XLSX.utils.book_new();
  const ws1 = generateRelationalTemplate1Worksheet(orch, currency);
  XLSX.utils.book_append_sheet(wb, ws1, 'Approved Menu');
  if (orch.hasSharedGroups) {
    const ws2 = generateSharedModifierSheet(orch.sharedGroups, currency);
    XLSX.utils.book_append_sheet(wb, ws2, 'Shared Modifier Library');
  }
  XLSX.writeFile(wb, `${filename}.xlsx`);
};

export const exportRelationalTemplate2 = (profile: MenuProfile, filename: string, currency: string) => {
  const orch = orchestrateExport(profile);
  const wb = XLSX.utils.book_new();
  const ws1 = generateRelationalTemplate2Worksheet(orch, currency);
  XLSX.utils.book_append_sheet(wb, ws1, 'Approved Menu');
  if (orch.hasSharedGroups) {
    const ws2 = generateSharedModifierSheet(orch.sharedGroups, currency);
    XLSX.utils.book_append_sheet(wb, ws2, 'Shared Modifier Library');
  }
  XLSX.writeFile(wb, `${filename}.xlsx`);
};

export const exportRelationalBoth = (profile: MenuProfile, filename: string, currency: string) => {
  const orch = orchestrateExport(profile);
  const wb = XLSX.utils.book_new();
  const ws1 = generateRelationalTemplate1Worksheet(orch, currency);
  XLSX.utils.book_append_sheet(wb, ws1, 'Detailed Menu');
  const ws2 = generateRelationalTemplate2Worksheet(orch, currency);
  XLSX.utils.book_append_sheet(wb, ws2, 'Aggregator Menu');
  if (orch.hasSharedGroups) {
    const ws3 = generateSharedModifierSheet(orch.sharedGroups, currency);
    XLSX.utils.book_append_sheet(wb, ws3, 'Shared Modifier Library');
  }
  XLSX.writeFile(wb, `${filename}.xlsx`);
};

// ─── Buffer variants (return ArrayBuffer, for embedding into ZIP) ────────────

export const exportRelationalTemplate1Buffer = (profile: MenuProfile, currency: string): ArrayBuffer => {
  const orch = orchestrateExport(profile);
  const wb = XLSX.utils.book_new();
  const ws1 = generateRelationalTemplate1Worksheet(orch, currency);
  XLSX.utils.book_append_sheet(wb, ws1, 'Approved Menu');
  if (orch.hasSharedGroups) {
    const ws2 = generateSharedModifierSheet(orch.sharedGroups, currency);
    XLSX.utils.book_append_sheet(wb, ws2, 'Shared Modifier Library');
  }
  return XLSX.write(wb, { bookType: 'xlsx', type: 'array' }) as ArrayBuffer;
};

export const exportRelationalTemplate2Buffer = (profile: MenuProfile, currency: string): ArrayBuffer => {
  const orch = orchestrateExport(profile);
  const wb = XLSX.utils.book_new();
  const ws1 = generateRelationalTemplate2Worksheet(orch, currency);
  XLSX.utils.book_append_sheet(wb, ws1, 'Approved Menu');
  if (orch.hasSharedGroups) {
    const ws2 = generateSharedModifierSheet(orch.sharedGroups, currency);
    XLSX.utils.book_append_sheet(wb, ws2, 'Shared Modifier Library');
  }
  return XLSX.write(wb, { bookType: 'xlsx', type: 'array' }) as ArrayBuffer;
};
